import { useEffect, useState } from 'react'

/** Intercom's launcher, in its lightweight and full builds. */
const INTERCOM_LAUNCHER = '.intercom-lightweight-app-launcher, .intercom-launcher-frame, .intercom-launcher'
/** The roots Intercom mounts under <body>, watched for the launcher being restyled. */
const INTERCOM_ROOTS = '.intercom-lightweight-app, #intercom-container'
const EDGE = 24
const GAP = 12

interface LauncherPosition {
    right: number
    bottom: number
}

const CORNER: LauncherPosition = { right: EDGE, bottom: EDGE }

/** Where an icon of `size` px sits: right above Intercom's launcher when one is on screen, else in the corner. */
function measure(size: number): LauncherPosition {
    const launcher = document.querySelector<HTMLElement>(INTERCOM_LAUNCHER)
    const rect = launcher?.getBoundingClientRect()
    if (!rect || rect.width === 0 || rect.height === 0) return CORNER
    return {
        right: Math.round(window.innerWidth - rect.right + (rect.width - size) / 2),
        bottom: Math.round(window.innerHeight - rect.top + GAP),
    }
}

/**
 * Keeps the general agent's icon clear of Intercom's chat bubble. Intercom boots and shuts down per page, so the
 * bubble is measured when it is added, removed or restyled, and on resize; nothing runs while the page is idle.
 * `frozen` keeps the last place, as while the panel is open Intercom's launcher is hidden and the icon must not jump.
 */
export function useLauncherPosition(size: number, frozen: boolean): LauncherPosition {
    const [position, setPosition] = useState<LauncherPosition>(CORNER)

    useEffect(() => {
        if (frozen) return
        let frame = 0
        const update = () => {
            cancelAnimationFrame(frame)
            frame = requestAnimationFrame(() =>
                setPosition((prev) => {
                    const next = measure(size)
                    return prev.right === next.right && prev.bottom === next.bottom ? prev : next
                })
            )
        }
        // Intercom mounts its root as a child of <body>: watch <body>'s children for it coming and going, and the root
        // itself for the launcher being shown, hidden or moved. The app's own DOM is never observed.
        const watched = new WeakSet<Element>()
        const observer = new MutationObserver(() => {
            watchIntercom()
            update()
        })
        const watchIntercom = () =>
            document.querySelectorAll(INTERCOM_ROOTS).forEach((root) => {
                if (watched.has(root)) return
                watched.add(root)
                observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] })
            })
        observer.observe(document.body, { childList: true })
        watchIntercom()
        update()
        window.addEventListener('resize', update)
        return () => {
            cancelAnimationFrame(frame)
            observer.disconnect()
            window.removeEventListener('resize', update)
        }
    }, [size, frozen])

    return position
}
